import React from 'react'
import * as AppDefinitions from '../../../App.d'
import {
	AccountXeroOrg,
	Calendar as CalendarData,
	Employee,
	EmployeeLeaveBalance,
	LeaveApplication,
	LeaveType,
} from '../../../../../back-end/utilities/apiDefinitions'
import { Row, Col, FormLabel, ButtonGroup, Dropdown, DropdownButton, Form, Table, Button, OverlayTrigger, Tooltip } from 'react-bootstrap'
import { TimePeriodPicker } from '../../oversight/timePeriodPicker'
import * as I from '../../../utilities/me'
import moment from 'moment'
import { X } from 'react-feather'
import { PieChart } from 'react-minimal-pie-chart'
import { getDataForPieChart, isLeaveWithinPeriod, leaveApplicationIncludedUnits } from './overview'
import { displayLeaveBalance } from '../leaveBalances'

interface EmployeePerformanceOverviewProps {
	allLeaveApplications: LeaveApplication[] | []
	allXeroOrgLeaveApplications: LeaveApplication[] | []
	originalBalanceData: EmployeeLeaveBalance[]
	originalXeroOrgBalanceData: EmployeeLeaveBalance[]
	appState: AppDefinitions.AppState
	currentOrg: AccountXeroOrg
	xeroOrgCalendars: CalendarData[] | null
	currentCalendar: CalendarData
}

const defaultDateRange = {
	startDate: moment().subtract(30, 'days'),
	endDate: moment(),
}

interface EmployeeLeaveStats {
	name: string
	count: number
	duration: number
	balance: number
	afterWeekends: number
	calendarCount: number
	companyCount: number
	calendarDuration: number
	companyDuration: number
	calendarBalance: number
	companyBalance: number
	calendarAfterWeekends: number
	companyAfterWeekends: number
	unitType: string
}

const EmployeePerformanceOverview = (props: EmployeePerformanceOverviewProps) => {
	const [period, updatePeriod] = React.useState<{
		startDate: moment.Moment | null
		endDate: moment.Moment | null
	}>(defaultDateRange)
	const [employee, updateEmployee] = React.useState<Employee | undefined>(undefined)
	const [calendarEmployees, updateCalendarEmployees] = React.useState<Employee[] | []>([])
	const [employeeLeaveStats, updateEmployeeLeaveStats] = React.useState<{ [leaveTypeID: string]: EmployeeLeaveStats }>({})
	const [calculated, updateCalculated] = React.useState<boolean>(false)
	const [results, updateResults] = React.useState<number | null>(null)

	React.useEffect(
		() => {
			const fetchData = async () => {
				updateCalendarEmployees(props.currentCalendar.employees!.sort((a, b) => (a.firstName! > b.firstName! ? 1 : -1)))
			}

			if (props.currentCalendar.calendarID !== '') {
				fetchData()
			}
		},
		// eslint-disable-next-line
		[props.appState.authState, props.currentCalendar.calendarID, props.currentOrg, props.currentCalendar, period]
	)

	const leavesInPeriod = React.useCallback(
		(type: 'employee' | 'leave' | 'xeroorg', id: string, period: { startDate: moment.MomentInput; endDate: moment.MomentInput }) => {
			if (type === 'xeroorg') {
				const leavesInPeriod = props.allXeroOrgLeaveApplications.filter((leave) => isLeaveWithinPeriod(leave, period))
				return leavesInPeriod.filter((leave) => leave.xeroOrgLeaveTypeID === id)
			} else {
				const leavesInPeriod = props.allLeaveApplications.filter((leave) => isLeaveWithinPeriod(leave, period))
				return type === 'employee'
					? leavesInPeriod.filter((leave) => leave.employeeID === id)
					: leavesInPeriod.filter((leave) => leave.xeroOrgLeaveTypeID === id)
			}
		},
		[props.allXeroOrgLeaveApplications, props.allLeaveApplications]
	)

	const leavesFollowingWeekend = React.useCallback((leaves: LeaveApplication[]) => {
		return leaves.filter((lapp) => moment(lapp.startDate!).day() === 1).length || 0 // 1 represents Monday
	}, [])

	const oneLeaveAppDuration = React.useCallback((leaveApp: LeaveApplication, periodStart: moment.Moment, periodEnd: moment.Moment): number => {
		return leaveApplicationIncludedUnits(props.currentOrg, leaveApp, periodStart, periodEnd)
	}, [])

	const leavesTotalDuration = React.useCallback(
		(leaves: LeaveApplication[], periodStart: moment.Moment, periodEnd: moment.Moment): number => {
			return leaves.reduce((total, leave) => {
				return total + oneLeaveAppDuration(leave, periodStart, periodEnd)
			}, 0)
		},
		[oneLeaveAppDuration]
	)

	const totalBalanceValue = React.useCallback(
		(totalValue: number, balances: EmployeeLeaveBalance[]) => (totalValue += balances.reduce((totalUnits, bl) => (totalUnits += Number(bl.value) || 0), 0)),
		[]
	)

	const calculateEmployeeStats = React.useCallback(
		(employeeID: string, period: { startDate: moment.MomentInput; endDate: moment.MomentInput }) => {
			const employeeLeaves = leavesInPeriod('employee', employeeID, period)
			const calUniqueEmployeeID = props.currentCalendar.employees!.filter((e) => e.isActive).map((e) => e.employeeID!)
			const allXeroEmployeeID = props.xeroOrgCalendars!.reduce(
				(employeeIDs: string[], currentCal) => employeeIDs.concat(currentCal.employees!.map((e) => e.employeeID!)),
				[]
			)
			const employeeStats = props.currentCalendar.leaveTypes!.reduce((result, currentValue: LeaveType) => {
				const currentEmployeeLeaveTypeLeaves = employeeLeaves.filter((l) => l.xeroOrgLeaveTypeID === currentValue.xeroOrgLeaveTypeID!)
				const currentXeroOrgLeaveTypeLeaves = leavesInPeriod('xeroorg', currentValue.xeroOrgLeaveTypeID!, period)
				const currentCalLeaveTypeLeaves = leavesInPeriod('leave', currentValue.xeroOrgLeaveTypeID!, period)

				const currentEmployeeLeaveTypeBalance = props.originalBalanceData.filter(
					(bl) => bl.employeeID === employeeID && bl.xeroOrgLeaveTypeID === currentValue.xeroOrgLeaveTypeID!
				)
				const currentCalLeaveTypeBalance = props.originalBalanceData.filter(
					(bl) => calUniqueEmployeeID.includes(bl.employeeID!) && bl.xeroOrgLeaveTypeID === currentValue.xeroOrgLeaveTypeID!
				)
				const currentOrgLeaveTypeBalance = props.originalBalanceData.filter(
					(bl) => allXeroEmployeeID.includes(bl.employeeID!) && bl.xeroOrgLeaveTypeID === currentValue.xeroOrgLeaveTypeID!
				)
				if (currentEmployeeLeaveTypeLeaves.length > 0) {
					result[currentValue.xeroOrgLeaveTypeID!] = {
						name: currentValue.name!,
						count: currentEmployeeLeaveTypeLeaves.length || 0,
						duration: parseFloat(
							displayLeaveBalance(
								leavesTotalDuration(currentEmployeeLeaveTypeLeaves, moment(period.startDate), moment(period.endDate)),
								currentValue.unitType!,
								props.currentOrg.xeroOrg!.showLeaveInDays!,
								props.currentOrg.xeroOrg!.hoursInDay!
							)
						),
						balance:
							currentEmployeeLeaveTypeBalance.length > 0
								? parseFloat(
										displayLeaveBalance(
											currentEmployeeLeaveTypeBalance[0].value!,
											currentValue.unitType!,
											props.currentOrg.xeroOrg!.showLeaveInDays!,
											props.currentOrg.xeroOrg!.hoursInDay!
										)
								  )
								: 0,
						afterWeekends: leavesFollowingWeekend(currentEmployeeLeaveTypeLeaves),
						calendarCount: currentCalLeaveTypeLeaves.length || 0,
						companyCount: currentXeroOrgLeaveTypeLeaves.length || 0,
						calendarDuration: parseFloat(
							displayLeaveBalance(
								leavesTotalDuration(currentCalLeaveTypeLeaves, moment(period.startDate), moment(period.endDate)),
								currentValue.unitType!,
								props.currentOrg.xeroOrg!.showLeaveInDays!,
								props.currentOrg.xeroOrg!.hoursInDay!
							)
						),
						companyDuration: parseFloat(
							displayLeaveBalance(
								leavesTotalDuration(currentXeroOrgLeaveTypeLeaves, moment(period.startDate), moment(period.endDate)),
								currentValue.unitType!,
								props.currentOrg.xeroOrg!.showLeaveInDays!,
								props.currentOrg.xeroOrg!.hoursInDay!
							)
						),
						calendarBalance: Math.round(
							parseFloat(
								displayLeaveBalance(
									totalBalanceValue(0, currentCalLeaveTypeBalance),
									currentValue.unitType!,
									props.currentOrg.xeroOrg!.showLeaveInDays!,
									props.currentOrg.xeroOrg!.hoursInDay!
								)
							)
						),
						companyBalance: Math.round(
							parseFloat(
								displayLeaveBalance(
									totalBalanceValue(0, currentOrgLeaveTypeBalance),
									currentValue.unitType!,
									props.currentOrg.xeroOrg!.showLeaveInDays!,
									props.currentOrg.xeroOrg!.hoursInDay!
								)
							)
						),
						calendarAfterWeekends: leavesFollowingWeekend(currentCalLeaveTypeLeaves),
						companyAfterWeekends: leavesFollowingWeekend(currentXeroOrgLeaveTypeLeaves),
						unitType: props.currentOrg.xeroOrg!.showLeaveInDays ? 'Days' : 'Hours',
					}
				}
				return result
			}, {} as { [leaveTypeID: string]: EmployeeLeaveStats })

			updateEmployeeLeaveStats(employeeStats)
			return employeeStats
		},
		[
			leavesInPeriod,
			leavesTotalDuration,
			leavesFollowingWeekend,
			totalBalanceValue,
			props.currentCalendar.leaveTypes,
			props.originalBalanceData,
			props.xeroOrgCalendars,
			props.currentCalendar.employees,
			props.xeroOrgCalendars,
		]
	)

	const organiseDataForPieChart = React.useCallback(
		(type: 'number' | 'duration') => {
			if (employeeLeaveStats && Object.keys(employeeLeaveStats).length !== 0) {
				const data = Object.values(employeeLeaveStats).reduce((result, current) => {
					result[current.name] = type === 'number' ? current.count : current.duration
					return result
				}, {} as { [name: string]: number })
				return data
			}
			return {}
		},
		[employeeLeaveStats]
	)

	const presentDetails = () => {
		// Calculate values
		const employeeStats = calculateEmployeeStats(employee ? employee.employeeID! : '', period)
		updateCalculated(true)
		updateResults(Object.keys(employeeStats!).length)
	}

	return (
		<Row className="calendar-alerts-card ">
			<Col>
				<Row style={{ paddingLeft: '20px' }}>
					<Col xs={'auto'} sm={2} md={2} lg={'auto'} className={'emails-label'}>
						Employee Performance Overview
					</Col>
					<TimePeriodPicker
						dates={period}
						id={'period'}
						noPeriod={true}
						handleDatesChange={(dates) => {
							updatePeriod(dates)
							updateEmployee(employee ? employee : undefined)
						}}
					/>
					<Col sm="auto">
						<DropdownButton
							as={ButtonGroup}
							id={'dropdown'}
							variant={'primary'}
							title={
								(employee as unknown as Employee)
									? employee!.alias
										? employee!.alias
										: `${employee!.firstName} ${employee!.lastName}`
									: 'Select employee'
							}
							onSelect={(eventKey) => {
								if (eventKey && calendarEmployees) {
									updateEmployee(calendarEmployees!.find((e: Employee) => e.employeeID === eventKey) || undefined)
								}
							}}
							alignRight
							className={!I.have('admin', props.appState.permission[props.appState.context]) ? 'no-hover' : ''}
						>
							{calendarEmployees
								? calendarEmployees.map((emp) => (
										<Dropdown.Item
											key={emp.employeeID}
											eventKey={emp.employeeID}
											active={(employee as unknown as Employee) ? emp.employeeID === employee!.employeeID : false}
										>
											{emp!.alias ? emp!.alias : `${emp!.firstName} ${emp!.lastName}`}
										</Dropdown.Item>
								  ))
								: 'No employees'}
						</DropdownButton>
						{employee ? (
							<Button
								className="circle"
								style={{ marginLeft: '5px' }}
								variant={'dark'}
								onClick={() => {
									updateEmployee(undefined)
									updateEmployeeLeaveStats({})
								}}
							>
								<X />
							</Button>
						) : null}
					</Col>
					<Col style={{ paddingTop: '3px' }}>
						<Button type="button" disabled={!(period.startDate && period.endDate)} style={{ padding: '3px 20px' }} onClick={() => presentDetails()}>
							Calculate
						</Button>
					</Col>
				</Row>

				<div
					style={{
						marginTop: '5px',
						paddingLeft: '20px',
						backgroundColor: 'white',
						paddingTop: '20px',
					}}
				>
					{period.startDate && period.endDate ? (
						<>
							{employee && calculated ? (
								<div style={{ marginRight: '15px' }}>
									<Form.Group style={{ padding: '30px' }}>
										<Table borderless responsive className="no-wrap">
											<thead>
												<tr className="stripe-row-body">
													<th className="header-sticky-top" style={{ fontWeight: 'bold' }}>
														Leave Type
													</th>
													<th className="header-sticky-top" style={{ fontWeight: 'bold' }}>
														Number
													</th>
													<th className="header-sticky-top" style={{ fontWeight: 'bold' }}>
														Duration
													</th>
													<th className="header-sticky-top" style={{ fontWeight: 'bold' }}>
														Payroll Balance
													</th>
													<th className="header-sticky-top" style={{ fontWeight: 'bold' }}>
														After Weekends
													</th>
													<th></th>
												</tr>
											</thead>
											<tbody>
												{employeeLeaveStats && results && results > 0 ? (
													Object.values(employeeLeaveStats).map((lt, index) => (
														<tr key={`${lt.name}-${index}`}>
															<td>{lt.name}</td>
															<td>
																<OverlayTrigger
																	placement="top"
																	overlay={
																		<Tooltip id="delete-tooltip" placement="top">
																			{`Total number of approved ${lt.name} requests for ${employee.firstName} ${employee.lastName} that occur during the selected date range.`}
																		</Tooltip>
																	}
																>
																	<span>Employee</span>
																</OverlayTrigger>
																: {lt.count}
																<br />
																<OverlayTrigger
																	placement="top"
																	overlay={
																		<Tooltip id="delete-tooltip" placement="top">
																			{`Total number of approved ${lt.name} requests in ${props.currentCalendar.fileName} calendar that occur during the selected date range.`}
																		</Tooltip>
																	}
																>
																	<span>Calendar</span>
																</OverlayTrigger>
																: {lt.calendarCount}
																<br />
																<OverlayTrigger
																	placement="top"
																	overlay={
																		<Tooltip id="delete-tooltip" placement="top">
																			{`Total number of approved ${lt.name} requests in ${
																				props.currentOrg.xeroOrg!.legalName
																			} that occur during the selected date range.`}
																		</Tooltip>
																	}
																>
																	<span>Org</span>
																</OverlayTrigger>
																: {lt.companyCount}
															</td>
															<td>
																<OverlayTrigger
																	placement="top"
																	overlay={
																		<Tooltip id="delete-tooltip" placement="top">
																			{`Total duration of ${lt.name} for ${employee.firstName} ${employee.lastName} that occur during the selected date range.`}
																		</Tooltip>
																	}
																>
																	<span>Employee</span>
																</OverlayTrigger>
																: {lt.duration} {lt.unitType}
																<br />
																<OverlayTrigger
																	placement="top"
																	overlay={
																		<Tooltip id="delete-tooltip" placement="top">
																			{`Total duration of ${lt.name} for ${props.currentCalendar.fileName} calendar that occur during the selected date range.`}
																		</Tooltip>
																	}
																>
																	<span>Calendar</span>
																</OverlayTrigger>
																: {lt.calendarDuration} {lt.unitType}
																<br />
																<OverlayTrigger
																	placement="top"
																	overlay={
																		<Tooltip id="delete-tooltip" placement="top">
																			{`Total duration of ${lt.name} for ${
																				props.currentOrg.xeroOrg!.legalName
																			} that occur during the selected date range.`}
																		</Tooltip>
																	}
																>
																	<span>Org</span>
																</OverlayTrigger>
																: {lt.companyDuration} {lt.unitType}
															</td>
															<td>
																<OverlayTrigger
																	placement="top"
																	overlay={
																		<Tooltip id="delete-tooltip" placement="top">
																			{`Remaining ${lt.name} for ${employee.firstName} ${employee.lastName} currently from Payroll.`}
																		</Tooltip>
																	}
																>
																	<span>Employee</span>
																</OverlayTrigger>
																: {lt.balance} {lt.unitType}
																<br />
																<OverlayTrigger
																	placement="top"
																	overlay={
																		<Tooltip id="delete-tooltip" placement="top">
																			{`Remaining ${lt.name} for ${props.currentCalendar.fileName} calendar currently from Payroll.`}
																		</Tooltip>
																	}
																>
																	<span>Calendar</span>
																</OverlayTrigger>
																: {lt.calendarBalance} {lt.unitType}
																<br />
																<OverlayTrigger
																	placement="top"
																	overlay={
																		<Tooltip id="delete-tooltip" placement="top">
																			{`Remaining ${lt.name} for ${
																				props.currentOrg.xeroOrg!.legalName
																			} currently from Payroll.`}
																		</Tooltip>
																	}
																>
																	<span>Org</span>
																</OverlayTrigger>
																: {lt.companyBalance} {lt.unitType}
															</td>
															<td>
																<OverlayTrigger
																	placement="top"
																	overlay={
																		<Tooltip id="delete-tooltip" placement="top">
																			{`Total number of approved ${lt.name} requests taken after a weekend for ${employee.firstName} ${employee.lastName} that occur during the selected date range.`}
																		</Tooltip>
																	}
																>
																	<span>Employee</span>
																</OverlayTrigger>
																: {lt.afterWeekends}
																<br />
																<OverlayTrigger
																	placement="top"
																	overlay={
																		<Tooltip id="delete-tooltip" placement="top">
																			{`Total number of approved ${lt.name} requests taken after a weekend for ${props.currentCalendar.fileName} calendar that occur during the selected date range.`}
																		</Tooltip>
																	}
																>
																	<span>Calendar</span>
																</OverlayTrigger>
																: {lt.calendarAfterWeekends}
																<br />
																<OverlayTrigger
																	placement="top"
																	overlay={
																		<Tooltip id="delete-tooltip" placement="top">
																			{`Total number of approved ${lt.name} requests taken after a weekend for ${
																				props.currentOrg.xeroOrg!.legalName
																			} that occur during the selected date range.`}
																		</Tooltip>
																	}
																>
																	<span>Org</span>
																</OverlayTrigger>
																: {lt.companyAfterWeekends}
															</td>
														</tr>
													))
												) : (
													<p style={{ margin: '5px' }}>No leave taken during this period.</p>
												)}
											</tbody>
										</Table>
									</Form.Group>
									{results && results > 0 ? (
										<Row
											style={{
												marginTop: '-5px',
												paddingLeft: '20px',
												backgroundColor: 'white',
												paddingTop: '20px',
											}}
										>
											<Col style={{ textAlign: 'center' }}>
												<PieChart
													data={getDataForPieChart(organiseDataForPieChart('number'), 0)}
													style={{ height: '230px' }}
													label={({ dataEntry }) => `${dataEntry.title!} (${dataEntry.value!})`}
													labelStyle={{
														fontSize: '5px',
													}}
													labelPosition={60}
													animate
												/>
												<FormLabel style={{ paddingTop: '20px', fontWeight: 'bold' }}>Number Of Applications</FormLabel>
											</Col>
											<Col style={{ textAlign: 'center' }}>
												<PieChart
													data={getDataForPieChart(organiseDataForPieChart('duration'), 1)}
													style={{ height: '230px' }}
													label={({ dataEntry }) => `${dataEntry.title!} (${dataEntry.value!})`}
													labelStyle={{
														fontSize: '5px',
													}}
													labelPosition={60}
													animate
												/>
												<FormLabel style={{ paddingTop: '20px', fontWeight: 'bold' }}>{`Duration (${
													props.currentOrg.xeroOrg!.showLeaveInDays ? 'Days' : 'Hours'
												})`}</FormLabel>
											</Col>
										</Row>
									) : null}
								</div>
							) : (
								<Row
									style={{
										marginTop: '5px',
										paddingLeft: '20px',
										backgroundColor: 'white',
										paddingTop: '20px',
										paddingBottom: '50px',
									}}
								>
									<p style={{ fontWeight: 'lighter' }}>
										{employee ? 'Press Calculate to display a summary for this period.' : 'Select an employee for the performance summary.'}
									</p>
								</Row>
							)}
						</>
					) : (
						<Row
							style={{
								marginTop: '5px',
								paddingLeft: '20px',
								backgroundColor: 'white',
								paddingTop: '20px',
								paddingBottom: '50px',
							}}
						>
							<p style={{ fontWeight: 'lighter' }}>Select a date range in order to start displaying an employee performance summary.</p>
						</Row>
					)}
				</div>
			</Col>
		</Row>
	)
}

export default EmployeePerformanceOverview
